import { diffLines } from 'diff';

export async function fetchRevision(id) {
    // function_revisions._id is numeric, but ids coming from a <select> value or a url
    // query param are always strings - match both forms rather than relying on the
    // caller to know which one Mongo actually stored.
    const candidates = /^\d+$/.test(id) ? [id, Number(id)] : [id];
    const response = await fetch('/api/data/search/function_revisions', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            query: { _id: { $in: candidates } },
            limit: 1,
        }),
    });
    const result = await response.json();
    if (result.error) {
        return result;
    }
    return { result: (result.results && result.results[0]) || null };
}

export async function fetchRouting() {
    const response = await fetch('/api/data/search/function_routing', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: {} }),
    });
    const result = await response.json();
    if (result.error) {
        return result;
    }
    const routing = {};
    for (const doc of result.results) {
        routing[doc._id] = doc;
    }
    return { result: routing };
}

// target is "dev" or "prod". exists tells us whether that pointer document is already
// there - /api/data/update/:collection does a plain updateMany with no upsert, so if the
// pointer doesn't exist yet it would silently match nothing and we need to create it
// directly instead.
export async function setRoutingPointer(target, revisionId, admin, exists) {
    const updatedAt = new Date().toISOString();
    const response = await fetch(exists ? '/api/data/update/function_routing' : '/api/data/add/function_routing', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exists ? {
            query: { _id: target },
            update: {
                $set: {
                    version: revisionId,
                    updated_at: updatedAt,
                    updated_by: admin || 'unknown',
                },
                // writer_id is intentionally omitted/unset - see doc.md section 2.
                $unset: { writer_id: '' },
            },
        } : {
            add: {
                _id: target,
                version: revisionId,
                updated_at: updatedAt,
                updated_by: admin || 'unknown',
            },
        }),
    });
    try {
        return await response.json();
    } catch (e) {
        return { error: `Request failed: ${response.status} ${response.statusText}` };
    }
}

export async function searchRevisionSummaries(limit = 100) {
    const response = await fetch('/api/data/search/function_revisions', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            query: {},
            sort: { _id: -1 },
            limit,
            // Keep the list light - full lua code is fetched per-revision on demand.
            project: { functions: 0, jobs: 0, mods: 0 },
        }),
    });
    return response.json();
}

const CHUNK_KINDS = ['functions', 'jobs', 'mods'];

// Lua can be saved with CRLF line endings (e.g. edited on Windows) - normalize to LF so
// revisions that only differ by line ending don't show as changed everywhere.
function normalizeLineEndings(text) {
    return text.replace(/\r\n/g, '\n');
}

function chunksByName(revision) {
    const chunks = new Map();
    if (!revision) {
        return chunks;
    }
    for (const kind of CHUNK_KINDS) {
        for (const chunk of revision[kind] || []) {
            chunks.set(`${kind}/${chunk.name}`, normalizeLineEndings(chunk.lua || ''));
        }
    }
    return chunks;
}

function countChangedLines(oldValue, newValue) {
    let added = 0;
    let removed = 0;
    for (const part of diffLines(oldValue, newValue)) {
        if (part.added) {
            added += part.count;
        } else if (part.removed) {
            removed += part.count;
        }
    }
    return { added, removed };
}

// One entry per function/job/mod present in either revision (including unchanged ones),
// sorted so entries naturally group by kind (functions/, jobs/, mods/) then by name.
export function diffRevisionChunks(oldRevision, newRevision) {
    const oldChunks = chunksByName(oldRevision);
    const newChunks = chunksByName(newRevision);
    const names = new Set([...oldChunks.keys(), ...newChunks.keys()]);
    return [...names]
        .sort()
        .map(name => {
            const oldValue = oldChunks.get(name) || '';
            const newValue = newChunks.get(name) || '';
            const changed = oldValue !== newValue;
            const { added, removed } = changed ? countChangedLines(oldValue, newValue) : { added: 0, removed: 0 };
            return { name, oldValue, newValue, changed, added, removed };
        });
}
